
// Includes the isdataready tag for app overlays.
// src/Store/optionsStore.js
import { create } from 'zustand';
import {
  etDateKey,
  isDuringMarketHours,
} from '../Utils/marketHours';
import { seriesGapRangesForDate } from '../Utils/seriesPresentation';
import { STRIKE_WINDOW_PTS } from '../Utils/strikeFlow';

/** ---------- State shape ---------- */
const initial = {
  // legacy row snapshot support (tables etc.)
  rowsBySym: {},        // { [sym]: latestRow }
  spot: null,
  // True only once a genuine live WSS message (or playback frame) has populated
  // rowsBySym this session - NOT set by dailyCache's boot-time restore. Without
  // this gate, captureChainSnapshotAt could fire (bookmapSampler samples
  // immediately on mount) using rowsBySym still holding whatever was last
  // persisted, possibly hours stale, before the WSS connection has delivered
  // anything.
  hasLiveOptionsData: false,

  // ET date of the session `rowsBySym` currently holds. Stamped by EVERY write
  // that populates the map -- each live payload, and dailyCache's boot restore
  // with the date it was stored under.
  //
  // It has to cover live writes, not just restores: a tab left open across the
  // close carries yesterday's rows in memory with no restore involved, and every
  // guard that asks "did this come from a restore?" waves those through. Asking
  // "what day is this chain?" is the question that actually distinguishes them.
  // `indexBars` answers the same question from the bars themselves; rows carry
  // no timestamp, so the store stamps it.
  rowsTradingDate: null,

  // connection
  status: 'disconnected',
  lastObservationAt: null,
  disconnectedAt: null,
  liveGapRanges: [],
  version: 0,

  // server-fed minute aggregation (wire format)
  // { "YYYY-MM-DD HH:mm": { ... } }
  minuteAgg: {},

  // Rolling per-strike chain snapshots for the strike x time heatmap / flow book.
  // Each entry: { t: minuteEpochMs, spot, hydrationFrom, hydrationTo, byStrike: { [strike]: { g, d, vol, nv, cnv, pnv, cvol, pvol, hasLegSplit, bv, sv, gb, gs, db, ds, dbBull, dbBear, dsBull, dsBear } } }
  // g/d = net gamma/delta. The rest are cumulative session fields.
  // used to derive per-minute flow by diffing adjacent snapshots.
  // This is the time dimension rowsBySym lacks; sampled by the heatmap widget.
  chainSnaps: [],
  flowCalculationSnaps: null,
  chainSnapsFallback: false,
  chainSnapshotHydration: {
    lifecycle: 'idle',
    source: 'none',
    tradingDate: null,
    hasRenderableSeries: false,
  },

  /** NEW: gate the full-app overlay off this */
  isDataReady: false,              // <- flips true on first data
  firstDataAt: null,               // <- optional: timestamp of first data
};

// Cap on retained heatmap snapshots for MISD's inclusive 09:30-16:00 window.
const MAX_CHAIN_SNAPS = 400;

const BANDS = ['ITM', 'ATM', 'OTM'];

/** ---------- Helpers (pure) ---------- */
function emptyEntry() {
  return {
    sums:  { dDelta:0, dTheta:0, dVega:0, sumS:0, nS:0 },
    bands: {
      ITM:{ dDelta:0, dTheta:0, dVega:0, sumIV:0, nIV:0 },
      ATM:{ dDelta:0, dTheta:0, dVega:0, sumIV:0, nIV:0 },
      OTM:{ dDelta:0, dTheta:0, dVega:0, sumIV:0, nIV:0 },
    }
  };
}
function cloneEntry(e) {
  return {
    sums: { ...e.sums },
    bands: {
      ITM: { ...e.bands.ITM },
      ATM: { ...e.bands.ATM },
      OTM: { ...e.bands.OTM },
    },
  };
}
function sanitizeAgg(aggIn) {
  const out = {};
  const keys = Object.keys(aggIn || {}).sort();
  for (const k of keys) {
    const src = aggIn[k] || {};
    const e = emptyEntry();
    // sums
    for (const f of ['dDelta','dTheta','dVega','sumS','nS']) {
      const v = Number(src?.sums?.[f]);
      e.sums[f] = Number.isFinite(v) ? v : 0;
    }
    // bands
    for (const band of BANDS) {
      const sb = src?.bands?.[band] || {};
      for (const f of ['dDelta','dTheta','dVega','sumIV','nIV']) {
        const v = Number(sb?.[f]);
        e.bands[band][f] = Number.isFinite(v) ? v : 0;
      }
    }
    out[k] = e;
  }
  return out;
}
function pruneAgg(agg, keep) {
  if (!keep || keep <= 0) return agg;
  const keys = Object.keys(agg).sort();
  if (keys.length <= keep) return agg;
  const next = {};
  for (const k of keys.slice(-keep)) next[k] = agg[k];
  return next;
}

function snapshotStrikeCount(snap = {}) {
  return Object.keys(snap.byStrike || {}).length;
}

function snapshotTotalVolume(snap = {}) {
  return Object.values(snap.byStrike || {}).reduce((sum, cell) => {
    const vol = Number(cell?.vol);
    return sum + (Number.isFinite(vol) ? vol : 0);
  }, 0);
}

export function isSameMinuteCoverageRegression(previousSnap, nextSnap) {
  return snapshotStrikeCount(nextSnap) < snapshotStrikeCount(previousSnap) ||
    snapshotTotalVolume(nextSnap) < snapshotTotalVolume(previousSnap);
}

function hasSplitLegFields(cell = {}) {
  return cell.hasLegSplit === true &&
    ['cnv', 'pnv', 'cvol', 'pvol'].every((field) => Number.isFinite(Number(cell[field])));
}

export function splitFieldsMatchCombinedCell(splitCell, combinedCell) {
  if (!combinedCell) return false;
  const values = [
    splitCell.nv,
    splitCell.vol,
    splitCell.cnv,
    splitCell.pnv,
    splitCell.cvol,
    splitCell.pvol,
    combinedCell.nv,
    combinedCell.vol,
  ].map(Number);
  if (!values.every(Number.isFinite)) return false;
  const [nextNet, nextVolume, callNet, putNet, callVolume, putVolume, previousNet, previousVolume] = values;
  return nextNet === previousNet &&
    nextVolume === previousVolume &&
    callNet + putNet === previousNet &&
    callVolume + putVolume === previousVolume;
}

export function upgradeLegacySplitCells(previousSnap, nextSnap, { allowReplay = false } = {}) {
  if (!previousSnap.hydrationBoundaryTail && !allowReplay) return null;
  const byStrike = { ...(previousSnap.byStrike || {}) };
  let upgraded = false;
  for (const [strike, nextCell] of Object.entries(nextSnap.byStrike || {})) {
    const previousCell = byStrike[strike];
    if (
      !hasSplitLegFields(nextCell) ||
      previousCell?.hasLegSplit === true ||
      !splitFieldsMatchCombinedCell(nextCell, previousCell)
    ) continue;
    byStrike[strike] = {
      ...previousCell,
      cnv: nextCell.cnv,
      pnv: nextCell.pnv,
      cvol: nextCell.cvol,
      pvol: nextCell.pvol,
      hasLegSplit: true,
    };
    upgraded = true;
  }
  return upgraded ? { ...previousSnap, byStrike } : null;
}

// Mirrors the chain-snapshot service's FlowState._is_current_expiration (Python):
// the flows WSS multiplexes multiple SPXW expirations sharing the same strikes,
// so a stale/other-expiration row would otherwise sum into the same byStrike
// cell as today's 0-DTE contract. Symbols without a parseable SPXW+date marker
// (e.g. simplified fixtures) pass through unfiltered rather than being rejected.
function isCurrentExpirationSymbol(sym, tradingDateKey) {
  const symbol = String(sym || '');
  const markerIndex = symbol.indexOf('SPXW');
  if (markerIndex === -1) return true;
  const raw = symbol.slice(markerIndex + 4, markerIndex + 10);
  if (!/^\d{6}$/.test(raw)) return true;
  const expirationKey = `20${raw.slice(0, 2)}-${raw.slice(2, 4)}-${raw.slice(4, 6)}`;
  return expirationKey === tradingDateKey;
}

export function buildChainSnapshot(rowsBySym, spot, nowMs) {
  if (!Number.isFinite(spot)) return null;
  const tradingDateKey = etDateKey(nowMs);
  const byStrike = {};
  const invalidLegSplitStrikes = new Set();
  let hasAny = false;
  for (const sym in rowsBySym || {}) {
    const row = rowsBySym[sym];
    if (!isCurrentExpirationSymbol(row?.sym, tradingDateKey)) continue;
    const strike = Number(row?.strike);
    if (!Number.isFinite(strike) || Math.abs(strike - spot) > STRIKE_WINDOW_PTS) continue;
    const isCall = row.optionType === 'C';
    const isPut = row.optionType === 'P';
    if (!isCall && !isPut) {
      invalidLegSplitStrikes.add(strike);
      if (byStrike[strike]) byStrike[strike].hasLegSplit = false;
      continue;
    }

    const deltaBuy = Number(row.cumulativeDeltaBuy) || 0;
    const deltaSell = Number(row.cumulativeDeltaSell) || 0;
    const cell = byStrike[strike] || {
      g: 0, d: 0, vol: 0, nv: 0, cnv: 0, pnv: 0, cvol: 0, pvol: 0,
      hasLegSplit: !invalidLegSplitStrikes.has(strike), bv: 0, sv: 0, gb: 0, gs: 0, db: 0, ds: 0,
      dbBull: 0, dbBear: 0, dsBull: 0, dsBear: 0,
    };
    const volume = Number(row.accumulatedVolume) || 0;
    const netVol = Number(row.totalNetVol) || 0;
    cell.g += Number(row.cumulativeGammaNet) || 0;
    cell.d += Number(row.cumulativeDeltaNet) || 0;
    cell.vol += volume;
    cell.nv += netVol;
    if (isCall) {
      cell.cvol += volume;
      cell.cnv += netVol;
    } else {
      cell.pvol += volume;
      cell.pnv += netVol;
    }
    cell.bv += Number(row.cumulativeBuyVol) || 0;
    cell.sv += Number(row.cumulativeSellVol) || 0;
    cell.gb += Number(row.cumulativeGammaBuy) || 0;
    cell.gs += Number(row.cumulativeGammaSell) || 0;
    cell.db += deltaBuy;
    cell.ds += deltaSell;
    cell.dbBull += Math.max(0, deltaBuy);
    cell.dbBear += Math.max(0, -deltaBuy);
    cell.dsBull += Math.max(0, -deltaSell);
    cell.dsBear += Math.max(0, deltaSell);
    byStrike[strike] = cell;
    hasAny = true;
  }
  if (!hasAny) return null;

  return {
    t: Math.floor(nowMs / 60000) * 60000,
    sourceT: nowMs,
    spot,
    hydrationFrom: spot - STRIKE_WINDOW_PTS,
    hydrationTo: spot + STRIKE_WINDOW_PTS,
    byStrike,
  };
}

export function chainSnapshotMinuteEnd(t) {
  return Math.floor(t / 60000) * 60000 + 59999;
}

export function stampImportedChainSnapshot(snapshot) {
  return Number.isFinite(snapshot.sourceT) || !Number.isFinite(snapshot.t)
    ? snapshot
    : {
      ...snapshot,
      sourceT: chainSnapshotMinuteEnd(snapshot.t),
      serviceSnapshotOverlay: true,
    };
}

export function replaceSameMinuteChainSnapshot(previous, snapshot) {
  return {
    ...snapshot,
    ...(previous.hydrationSeam ? { hydrationSeam: true } : {}),
    ...(previous.hydrationBoundaryTail ? { hydrationBoundaryTail: true } : {}),
  };
}

export function mergeSameMinuteChainSnapshot(previous, snapshot) {
  if (isSameMinuteCoverageRegression(previous, snapshot)) {
    const upgraded = upgradeLegacySplitCells(previous, snapshot);
    if (!upgraded) return previous;
    const minuteEnd = chainSnapshotMinuteEnd(previous.t);
    return {
      ...upgraded,
      sourceT: Math.max(previous.sourceT ?? minuteEnd, snapshot.sourceT ?? minuteEnd),
    };
  }
  return replaceSameMinuteChainSnapshot(previous, snapshot);
}

export function mergeChainSnapshotSeries(existing, snapshot) {
  if (!snapshot) return existing;
  const snaps = (existing || []).slice();
  const last = snaps[snaps.length - 1];
  const next = stampImportedChainSnapshot(snapshot);
  if (last && last.t === snapshot.t) {
    snaps[snaps.length - 1] = mergeSameMinuteChainSnapshot(
      stampImportedChainSnapshot(last),
      next,
    );
  } else {
    snaps.push(next);
  }
  if (snaps.length > MAX_CHAIN_SNAPS) {
    snaps.splice(0, snaps.length - MAX_CHAIN_SNAPS);
  }
  return snaps;
}

/** simple EMA */
function ema(arr, k) {
  if (k <= 1) return arr.slice();
  const alpha = 2 / (k + 1);
  let prev = arr[0] || 0;
  return arr.map((v, i) => {
    if (i === 0) return prev;
    prev = prev + alpha * ((v || 0) - prev);
    return prev;
  });
}

/** ---------- Store ---------- */
export const useOptionsStore = create((set, get) => ({
  ...initial,

  /* connection/status */
  setStatus: (status, at = Date.now()) => set((state) => ({
    status,
    disconnectedAt: status === 'disconnected'
      ? state.disconnectedAt ?? at
      : state.disconnectedAt,
  })),

  recordObservation: (at = Date.now()) => set((state) => {
    const observationAt = Number(at);
    if (!Number.isFinite(observationAt)) return state;
    const from = Number.isFinite(state.disconnectedAt)
      ? Math.floor(state.disconnectedAt / 60_000) * 60_000 + 60_000
      : null;
    const to = Math.floor(observationAt / 60_000) * 60_000;
    const gap = Number.isFinite(from)
      && to > from
      && etDateKey(from) === etDateKey(to)
      && isDuringMarketHours(from)
      && isDuringMarketHours(to)
      ? [{ from, to, source: 'chain' }]
      : [];
    const retainedGapRanges = seriesGapRangesForDate(state.liveGapRanges, observationAt);
    const liveGapRanges = gap.length || retainedGapRanges.length !== state.liveGapRanges.length
      ? [...retainedGapRanges, ...gap]
      : state.liveGapRanges;
    return {
      status: 'connected',
      lastObservationAt: observationAt,
      disconnectedAt: null,
      liveGapRanges,
    };
  }),

  /** NEW: manual setter (handy for tests or special cases) */
  setDataReady: (ready = true) =>
    set((state) =>
      state.isDataReady === ready
        ? {}
        : { isDataReady: ready, firstDataAt: ready && !state.firstDataAt ? Date.now() : state.firstDataAt }
    ),

  hydrateChainSnapshotCache: ({ snaps = [], source = 'browser-cache', tradingDate = null, fallback = false }) =>
    set({
      chainSnaps: snaps.map(stampImportedChainSnapshot),
      flowCalculationSnaps: null,
      chainSnapsFallback: fallback,
      chainSnapshotHydration: {
        lifecycle: 'fallback',
        source,
        tradingDate,
        hasRenderableSeries: snaps.length > 0,
      },
    }),

  beginChainSnapshotHydration: ({ tradingDate = null, resetMismatched = false } = {}) => set((state) => {
    const latestSnapshot = state.chainSnaps[state.chainSnaps.length - 1];
    const renderedTradingDate = Number.isFinite(latestSnapshot?.t)
      ? etDateKey(latestSnapshot.t)
      : state.chainSnapshotHydration.tradingDate;
    const mustReset = Boolean(
      resetMismatched
      && tradingDate
      && renderedTradingDate
      && renderedTradingDate !== tradingDate
    );
    if (!mustReset && state.chainSnapshotHydration.lifecycle === 'ready') return {};
    const chainSnaps = mustReset ? [] : state.chainSnaps;
    const hasRenderableSeries = chainSnaps.length > 0;
    const awaitsLocalSession = mustReset
      && state.chainSnapshotHydration.lifecycle === 'degraded';
    return {
      ...(mustReset ? {
        chainSnaps,
        flowCalculationSnaps: null,
        chainSnapsFallback: false,
      } : {}),
      chainSnapshotHydration: {
        ...state.chainSnapshotHydration,
        lifecycle: hasRenderableSeries ? 'fallback' : (awaitsLocalSession ? 'degraded' : 'loading'),
        source: mustReset
          ? (awaitsLocalSession ? 'awaiting-local-session' : 'awaiting-latest-session')
          : state.chainSnapshotHydration.source,
        tradingDate: tradingDate ?? state.chainSnapshotHydration.tradingDate,
        hasRenderableSeries,
      },
    };
  }),

  applyAuthoritativeChainSnapshotInit: ({ snaps = [], tradingDate = null, fallback = false }) => set({
    chainSnaps: snaps.map(stampImportedChainSnapshot),
    flowCalculationSnaps: null,
    chainSnapsFallback: fallback,
    chainSnapshotHydration: {
      lifecycle: fallback ? 'fallback' : 'ready',
      source: 'authoritative-init',
      tradingDate,
      hasRenderableSeries: snaps.length > 0,
    },
  }),

  applyAuthoritativeChainSnapshotUpdate: ({
    snaps = [], tradingDate = null, complete = true,
  }) => set((state) => {
    const preserveReady = complete || state.chainSnapshotHydration.lifecycle === 'ready';
    return {
      chainSnaps: snaps.map(stampImportedChainSnapshot),
      flowCalculationSnaps: null,
      chainSnapsFallback: false,
      chainSnapshotHydration: {
        ...state.chainSnapshotHydration,
        lifecycle: preserveReady ? 'ready' : 'degraded',
        source: complete
          ? 'authoritative-update'
          : (preserveReady ? state.chainSnapshotHydration.source : 'authoritative-update-pending-init'),
        tradingDate: tradingDate ?? state.chainSnapshotHydration.tradingDate,
        hasRenderableSeries: snaps.length > 0,
      },
    };
  }),

  applyReplayChainSnapshotProjection: ({
    rowsBySym, tradingDate, spot, snaps = [], flowCalculationSnaps = null, hasOptionsData,
  }) => set({
    rowsBySym,
    rowsTradingDate: tradingDate,
    spot,
    chainSnaps: snaps,
    flowCalculationSnaps,
    chainSnapsFallback: false,
    chainSnapshotHydration: {
      lifecycle: 'ready',
      source: 'replay',
      tradingDate,
      hasRenderableSeries: snaps.length > 0,
    },
    status: 'replay',
    isDataReady: hasOptionsData,
    hasLiveOptionsData: hasOptionsData,
  }),

  degradeChainSnapshotHydration: ({ source = 'service-unavailable' } = {}) => set((state) => {
    if (state.chainSnapshotHydration.lifecycle === 'ready') return {};
    return {
      chainSnapshotHydration: {
        ...state.chainSnapshotHydration,
        lifecycle: 'degraded',
        source,
        hasRenderableSeries: state.chainSnaps.length > 0,
      },
    };
  }),

  /* ---------------- Legacy rows (no client aggregation) ---------------- */
  applyInit: (rows = []) => set((state) => {
    const rowsBySym = {};
    let spot = state.spot;
    for (const r of rows) {
      if (!r?.sym) continue;
      rowsBySym[r.sym] = r;
      if (Number.isFinite(r.S)) spot = r.S;
    }
    // After the close the WSS still sends an `init`, but with no rows. The chain
    // is showing the last session restored from dailyCache at that point, so
    // blanking rowsBySym here is what emptied the table overnight.
    if (!Object.keys(rowsBySym).length) return {};
    return {
      rowsBySym,
      spot,
      version: state.version + 1,
      hasLiveOptionsData: true,
      rowsTradingDate: etDateKey(Date.now()),
      ...(!state.isDataReady
        ? { isDataReady: true, firstDataAt: state.firstDataAt ?? Date.now() }
        : {}),
    };
  }),

  applyRow: (row) => set((state) => {
    if (!row?.sym) return {};
    // Rows from an EARLIER session must not merge into today's first row,
    // however they got here -- restored at boot, or simply carried in memory by
    // a tab left open across the close. Rows from today merge normally.
    const today = etDateKey(Date.now());
    const staleRows = Boolean(state.rowsTradingDate && state.rowsTradingDate !== today);
    const rowsBySym = { ...(staleRows ? {} : state.rowsBySym), [row.sym]: row };
    // Spot rolls over with the rows. Carrying the previous session's spot into a
    // chain that no longer contains it re-mixes the dates the line above just
    // separated, and every spot-relative view (ATM window, strike bands) reads
    // off it. Null until this session quotes one.
    const spot = Number.isFinite(row.S) ? row.S : (staleRows ? null : state.spot);
    const firstHit = !state.isDataReady;
    return {
      rowsBySym,
      spot,
      version: state.version + 1,
      hasLiveOptionsData: true,
      rowsTradingDate: today,
      ...(firstHit ? { isDataReady: true, firstDataAt: state.firstDataAt ?? Date.now() } : {}),
    };
  }),

  /* ---------------- Server-fed minute aggregation ---------------- */
  applyAggSnapshot: ({ minuteAgg = {}, spot = null }, keepBars) => {
    const snap = sanitizeAgg(minuteAgg);
    const pruned = keepBars ? pruneAgg(snap, keepBars) : snap;
    const gotData = Object.keys(pruned).length > 0 || Number.isFinite(spot);
    return set((state) => ({
      minuteAgg: pruned,
      spot: spot ?? state.spot,
      version: state.version + 1,
      ...(gotData && !state.isDataReady
        ? { isDataReady: true, firstDataAt: state.firstDataAt ?? Date.now() }
        : {}),
    }));
  },

  applyAggPatch: ({ bucket, sums = {}, bands = {} }, keepBars = 125) =>
    set((state) => {
      if (!bucket) return {};
      const curr = state.minuteAgg[bucket] ? cloneEntry(state.minuteAgg[bucket]) : emptyEntry();

      // sums (additive)
      for (const f of ['dDelta','dTheta','dVega','sumS','nS']) {
        const add = Number(sums[f]);
        if (Number.isFinite(add) && add !== 0) curr.sums[f] += add;
      }

      // bands (additive)
      for (const band of BANDS) {
        const src = bands[band];
        if (!src) continue;
        for (const f of ['dDelta','dTheta','dVega','sumIV','nIV']) {
          const add = Number(src[f]);
          if (Number.isFinite(add) && add !== 0) curr.bands[band][f] += add;
        }
      }

      // write & prune
      const minuteAgg = { ...state.minuteAgg, [bucket]: curr };
      const pruned = pruneAgg(minuteAgg, keepBars);

      const firstHit = !state.isDataReady; // flip once on first patch
      return {
        minuteAgg: pruned,
        version: state.version + 1,
        ...(firstHit ? { isDataReady: true, firstDataAt: state.firstDataAt ?? Date.now() } : {}),
      };
    }),

  /* ---------------- Selectors for widgets ---------------- */
  getDeltaFlowGrid: (lookback = 120, smooth = 5) => {
    const { minuteAgg } = get();
    const bucketsAll = Object.keys(minuteAgg).sort();
    if (!bucketsAll.length) return { buckets: [], byBand: { ITM: [], ATM: [], OTM: [] } };

    const buckets = bucketsAll.slice(-lookback);
    const byBand = {};
    for (const band of BANDS) {
      const raw = buckets.map(t => minuteAgg[t]?.bands?.[band]?.dDelta || 0);
      const sm  = ema(raw, smooth);
      byBand[band] = buckets.map((t, i) => ({ t, value: raw[i], ema: sm[i] }));
    }
    return { buckets, byBand };
  },

  getAggRowsForWidgets: () => {
    const { minuteAgg } = get();
    const buckets = Object.keys(minuteAgg).sort();
    if (!buckets.length) return [];

    const cum = {
      ITM: { dT:0, dV:0, dD:0 },
      ATM: { dT:0, dV:0, dD:0 },
      OTM: { dT:0, dV:0, dD:0 },
    };
    let lastS = 0, lastIV = { ITM:0, ATM:0, OTM:0 };

    const out = [];
    for (const b of buckets) {
      const e = minuteAgg[b];
      const Sbar = e.sums.nS ? e.sums.sumS / e.sums.nS : lastS; lastS = Sbar;

      for (const band of BANDS) {
        const seg = e.bands[band];
        const meanIV = seg.nIV ? seg.sumIV / seg.nIV : lastIV[band]; lastIV[band] = meanIV;

        cum[band].dT += seg.dTheta;
        cum[band].dV += seg.dVega;
        cum[band].dD += seg.dDelta;

        out.push({
          type: 'row',
          time: `${b}:00`,
          sym:  `AGG:${band}`,
          S: Sbar,
          iv: meanIV,
          cumulativeThetaNet: cum[band].dT,
          cumulativeVegaNet:  cum[band].dV,
          cumulativeDeltaNet: cum[band].dD,
          optionType: null, strike: null,
          cumulativeBuyVol: null, cumulativeSellVol: null, totalNetVol: null,
          accumulatedVolume: null, cumulativeDeltaBuy: null, cumulativeDeltaSell: null,
          cumulativeGammaBuy: null, cumulativeGammaSell: null, cumulativeGammaNet: null,
          moneyness: band, distance: 0, intrinsic: null, tradeCount: null,
        });
      }
    }
    return out;
  },

  /* ---------------- Heatmap snapshot sampling ---------------- */
  // Materialize one per-strike net-greek snapshot from the CURRENT chain state.
  // Buckets by minute: if the latest snapshot is in the same minute it is
  // replaced (the in-progress minute keeps updating), otherwise a new row is
  // pushed. No-op until we have a spot and at least one option row.
  // Live sampler. chainSnaps is a regular-trading-hours time series; never snapshot
  // outside RTH. This matters because the last-trade-day cache (App.js / dailyCache)
  // hydrates spot + rowsBySym AFTER the close — without this guard the 60s/15s tick
  // would append bogus current-timestamp snapshots that the heatmap filters out of
  // its display but that still evict the real in-hours snaps via MAX_CHAIN_SNAPS,
  // blanking the heatmap. Playback bypasses this by calling captureChainSnapshotAt.
  captureChainSnapshot: () => {
    if (get().status === 'replay') return;
    if (!isDuringMarketHours(Date.now())) return;
    // RTH alone is not enough. At 09:30 the chain may still hold YESTERDAY's rows
    // -- retained across the close by design since TD065, restored or simply held
    // in memory -- and `hasLiveOptionsData` is true for them. Snapshotting that
    // stamps yesterday's chain with today's minute, and when chainSnapsFallback
    // is set it replaces the real fallback series with the one fabricated snap.
    if (get().rowsTradingDate !== etDateKey(Date.now())) return;
    const { chainSnapshotHydration } = get();
    if (
      ['loading', 'fallback'].includes(chainSnapshotHydration.lifecycle)
    ) return;
    get().captureChainSnapshotAt(Date.now());
  },

  // Like captureChainSnapshot but buckets by a caller-supplied epoch instead of
  // Date.now(), so playback can rebuild chainSnaps with the fixture's ORIGINAL
  // (weekday) timestamps — required because isDuringMarketHours filters weekends.
  // See [[feature-playback-recorder]].
  captureChainSnapshotAt: (nowMs) => set((state) => {
    if (!state.hasLiveOptionsData) return {};
    if (
      state.chainSnapsFallback
      && !['ready', 'degraded'].includes(state.chainSnapshotHydration.lifecycle)
    ) return {};
    const snapshot = buildChainSnapshot(state.rowsBySym, state.spot, nowMs);
    if (!snapshot) return {};
    const existing = state.chainSnapsFallback ? [] : state.chainSnaps;
    const nextSnaps = mergeChainSnapshotSeries(existing, snapshot);
    return {
      chainSnaps: nextSnaps,
      flowCalculationSnaps: null,
      chainSnapsFallback: false,
      ...(state.chainSnapshotHydration.lifecycle === 'degraded' ? {
        chainSnapshotHydration: {
          ...state.chainSnapshotHydration,
          source: 'local-sampler',
          tradingDate: etDateKey(nowMs),
          hasRenderableSeries: nextSnaps.length > 0,
        },
      } : {}),
    };
  }),

  /* ---------------- Utilities ---------------- */
  prune: (maxBuckets = 125) =>
    set((state) => ({ minuteAgg: pruneAgg(state.minuteAgg, maxBuckets) })),

  reset: () => set(initial),
}));




//New code that uses backend aggregations for delta flow data. Does not include the isdataready tag
// src/Store/optionsStore.js
// import { create } from 'zustand';
//
// /** ---------- State shape ---------- */
// const initial = {
//   // legacy row snapshot support (tables etc.)
//   rowsBySym: {},        // { [sym]: latestRow }
//   spot: null,
//
//   // connection
//   status: 'disconnected',
//   version: 0,
//
//   // server-fed minute aggregation (wire format)
//   // { "YYYY-MM-DD HH:mm": {
//   //     sums:  { dDelta, dTheta, dVega, sumS, nS },
//   //     bands: { ITM:{ dDelta,dTheta,dVega,sumIV,nIV }, ATM:{...}, OTM:{...} }
//   //   } }
//   minuteAgg: {},
//
//   /** NEW: gate the full-app overlay off this */
//   isDataReady: false,              // <- flips true on first data
// };
//
// const BANDS = ['ITM', 'ATM', 'OTM'];
//
// /** ---------- Helpers (pure) ---------- */
// function emptyEntry() {
//   return {
//     sums:  { dDelta:0, dTheta:0, dVega:0, sumS:0, nS:0 },
//     bands: {
//       ITM:{ dDelta:0, dTheta:0, dVega:0, sumIV:0, nIV:0 },
//       ATM:{ dDelta:0, dTheta:0, dVega:0, sumIV:0, nIV:0 },
//       OTM:{ dDelta:0, dTheta:0, dVega:0, sumIV:0, nIV:0 },
//     }
//   };
// }
// function cloneEntry(e) {
//   return {
//     sums: { ...e.sums },
//     bands: {
//       ITM: { ...e.bands.ITM },
//       ATM: { ...e.bands.ATM },
//       OTM: { ...e.bands.OTM },
//     },
//   };
// }
// function sanitizeAgg(aggIn) {
//   const out = {};
//   const keys = Object.keys(aggIn || {}).sort();
//   for (const k of keys) {
//     const src = aggIn[k] || {};
//     const e = emptyEntry();
//     // sums
//     for (const f of ['dDelta','dTheta','dVega','sumS','nS']) {
//       const v = Number(src?.sums?.[f]);
//       e.sums[f] = Number.isFinite(v) ? v : 0;
//     }
//     // bands
//     for (const band of BANDS) {
//       const sb = src?.bands?.[band] || {};
//       for (const f of ['dDelta','dTheta','dVega','sumIV','nIV']) {
//         const v = Number(sb?.[f]);
//         e.bands[band][f] = Number.isFinite(v) ? v : 0;
//       }
//     }
//     out[k] = e;
//   }
//   return out;
// }
// function pruneAgg(agg, keep) {
//   if (!keep || keep <= 0) return agg;
//   const keys = Object.keys(agg).sort();
//   if (keys.length <= keep) return agg;
//   const next = {};
//   for (const k of keys.slice(-keep)) next[k] = agg[k];
//   return next;
// }
//
// /** simple EMA */
// function ema(arr, k) {
//   if (k <= 1) return arr.slice();
//   const alpha = 2 / (k + 1);
//   let prev = arr[0] || 0;
//   return arr.map((v, i) => {
//     if (i === 0) return prev;
//     prev = prev + alpha * ((v || 0) - prev);
//     return prev;
//   });
// }
//
// /** ---------- Store ---------- */
// export const useOptionsStore = create((set, get) => ({
//   ...initial,
//
//   /* connection/status */
//   setStatus: (status) => set({ status }),
//
//   /* ---------------- Legacy rows (no client aggregation) ---------------- */
//   // Keep tables and any other row-based widgets happy; we just map latest rows.
//   applyInit: (rows = []) => set((state) => {
//     const rowsBySym = {};
//     let spot = state.spot;
//     for (const r of rows) {
//       if (!r?.sym) continue;
//       rowsBySym[r.sym] = r;
//       if (Number.isFinite(r.S)) spot = r.S;
//     }
//     return { rowsBySym, spot, version: state.version + 1 };
//   }),
//
//   applyRow: (row) => set((state) => {
//     if (!row?.sym) return {};
//     const rowsBySym = { ...state.rowsBySym, [row.sym]: row };
//     const spot = Number.isFinite(row.S) ? row.S : state.spot;
//     return { rowsBySym, spot, version: state.version + 1 };
//   }),
//
//   /* ---------------- Server-fed minute aggregation ---------------- */
//   // Replace the in-memory aggregation with a server snapshot.
//   // Optionally pass keepBars (e.g., 125 = 120 + 5 warmup) to prune on set.
//   applyAggSnapshot: ({ minuteAgg = {}, spot = null }, keepBars) => {
//     const snap = sanitizeAgg(minuteAgg);
//     const pruned = keepBars ? pruneAgg(snap, keepBars) : snap;
//     return set((state) => ({
//       minuteAgg: pruned,
//       spot: spot ?? state.spot,
//       version: state.version + 1,
//     }));
//   },
//
//   // Additive patch merge for a single bucket. Prunes to last N buckets.
//   // Usage: applyAggPatch(msg, keepBars) where keepBars = lookback + warmup (e.g., 125)
//
//   applyAggPatch: ({ bucket, sums = {}, bands = {} }, keepBars = 125) =>
//     set((state) => {
//       if (!bucket) return {};
//       const curr = state.minuteAgg[bucket] ? cloneEntry(state.minuteAgg[bucket]) : emptyEntry();
//
//       // sums (additive)
//       for (const f of ['dDelta','dTheta','dVega','sumS','nS']) {
//         const add = Number(sums[f]);
//         if (Number.isFinite(add) && add !== 0) curr.sums[f] += add;
//       }
//
//       // bands (additive)
//       for (const band of BANDS) {
//         const src = bands[band];
//         if (!src) continue;
//         for (const f of ['dDelta','dTheta','dVega','sumIV','nIV']) {
//           const add = Number(src[f]);
//           if (Number.isFinite(add) && add !== 0) curr.bands[band][f] += add;
//         }
//       }
//
//       // write & prune
//       const minuteAgg = { ...state.minuteAgg, [bucket]: curr };
//       const pruned = pruneAgg(minuteAgg, keepBars);
//
//       return { minuteAgg: pruned, version: state.version + 1 };
//     }),
//
//   /* ---------------- Selectors for widgets ---------------- */
//   // Used by DeltaFlowMap: returns { buckets, byBand } with raw and EMA series.
//   getDeltaFlowGrid: (lookback = 120, smooth = 5) => {
//     const { minuteAgg } = get();
//     const bucketsAll = Object.keys(minuteAgg).sort();
//     if (!bucketsAll.length) return { buckets: [], byBand: { ITM: [], ATM: [], OTM: [] } };
//
//     const buckets = bucketsAll.slice(-lookback);
//     const byBand = {};
//     for (const band of BANDS) {
//       const raw = buckets.map(t => minuteAgg[t]?.bands?.[band]?.dDelta || 0);
//       const sm  = ema(raw, smooth);
//       byBand[band] = buckets.map((t, i) => ({ t, value: raw[i], ema: sm[i] }));
//     }
//     return { buckets, byBand };
//   },
//
//   // Optional shim if older components expect "row-like" cumulative series.
//   getAggRowsForWidgets: () => {
//     const { minuteAgg } = get();
//     const buckets = Object.keys(minuteAgg).sort();
//     if (!buckets.length) return [];
//
//     const cum = {
//       ITM: { dT:0, dV:0, dD:0 },
//       ATM: { dT:0, dV:0, dD:0 },
//       OTM: { dT:0, dV:0, dD:0 },
//     };
//     let lastS = 0, lastIV = { ITM:0, ATM:0, OTM:0 };
//
//     const out = [];
//     for (const b of buckets) {
//       const e = minuteAgg[b];
//       const Sbar = e.sums.nS ? e.sums.sumS / e.sums.nS : lastS; lastS = Sbar;
//
//       for (const band of BANDS) {
//         const seg = e.bands[band];
//         const meanIV = seg.nIV ? seg.sumIV / seg.nIV : lastIV[band]; lastIV[band] = meanIV;
//
//         cum[band].dT += seg.dTheta;
//         cum[band].dV += seg.dVega;
//         cum[band].dD += seg.dDelta;
//
//         out.push({
//           type: 'row',
//           time: `${b}:00`,
//           sym:  `AGG:${band}`,
//           S: Sbar,
//           iv: meanIV,
//           cumulativeThetaNet: cum[band].dT,
//           cumulativeVegaNet:  cum[band].dV,
//           cumulativeDeltaNet: cum[band].dD,
//           optionType: null, strike: null,
//           cumulativeBuyVol: null, cumulativeSellVol: null, totalNetVol: null,
//           accumulatedVolume: null, cumulativeDeltaBuy: null, cumulativeDeltaSell: null,
//           cumulativeGammaBuy: null, cumulativeGammaSell: null, cumulativeGammaNet: null,
//           moneyness: band, distance: 0, intrinsic: null, tradeCount: null,
//         });
//       }
//     }
//     return out;
//   },
//
//   /* ---------------- Utilities ---------------- */
//   // Manual prune if needed (e.g., when toggling lookbacks)
//   prune: (maxBuckets = 125) =>
//     set((state) => ({ minuteAgg: pruneAgg(state.minuteAgg, maxBuckets) })),
//
//   reset: () => set(initial),
// }));
